import React from "react"
import { useSiteMetadata } from "../hooks/use-site-metadata.js"

export const SEO = ({ title, description, pathname, children }) => {
  const {
    title: defaultTitle,
    description: defaultDescription,
    image,
    logo,
    siteUrl,
    twitterUsername,
  } = useSiteMetadata()

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image}`,
    url: `${siteUrl}${pathname || ``}`,
    twitterUsername,
    logo: `${siteUrl}${logo}`,
  }

  const schemaOrgBrand = {
    "@context": "http://schema.org",
    "@type": "Brand",
    "@id": "https://www.amalgamhomes.com/#brand",
    name: "Amalgam Homes",
    url: "https://www.amalgamhomes.com",
    logo: seo.logo,
    slogan: "We are the amalgamation of the Aegean",
    description: defaultDescription,
    sameAs: [
      "https://www.facebook.com/amalgamhomes",
      "https://www.instagram.com/amalgamhomes/",
    ],
  }

  const schemaOrgOrganization = {
    "@context": "http://schema.org",
    "@type": "Organization",
    "@id": "https://www.amalgamhomes.com/#organization",
    url: "https://www.amalgamhomes.com",
    name: "Amalgam Homes",
    brand: schemaOrgBrand,
    email: "info@amalgamhomes.com",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+302106742743",
      contactType: "customer service",
    },
    logo: seo.logo,
    sameAs: [
      "https://www.facebook.com/amalgamhomes",
      "https://www.instagram.com/amalgamhomes/",
    ],
    // "potentialAction": [
    //   {
    //     "@type": "SearchAction",
    //     "target": {
    //       "@type": "EntryPoint",
    //       "urlTemplate": "https://www.amalgamhomes.com/?s={search_term_string}"
    //     },
    //     "query-input": "required name=search_term_string"
    //   }
    // ]
  }

  const schemaOrgImageLogo = {
    "@type": "ImageObject",
    "@id": "https://www.amalgamhomes.com/#logo",
    inLanguage: "en-US",
    url: seo.logo,
    contentUrl: seo.logo,
    width: 300,
    height: 300,
  }

  const schemaOrgImagePrimary = {
    "@type": "ImageObject",
    "@id": "https://www.amalgamhomes.com/#primaryimage",
    inLanguage: "en-US",
    url: seo.image,
    contentUrl: seo.image,
    width: 1200,
    height: 630,
  }

  const schemaOrgWebSite = {
    "@context": "http://schema.org",
    "@type": "WebSite",
    "@id": "https://www.amalgamhomes.com/#website",
    url: "https://www.amalgamhomes.com",
    name: "Amalgam Homes",
    description: "Luxury villas for rent in Greece",
    publisher: {
      "@id": "https://www.amalgamhomes.com/#organization",
    },
    image: seo.image,
    potentialAction: [
      {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate:
            "https://www.amalgamhomes.com/sitelink-searchbox?s={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    ],
  }

  const schemaOrgLocalBusiness = {
    "@context": "http://schema.org",
    "@type": "LocalBusiness",
    name: "Amalgam Homes",
    image: seo.image,
    url: "https://www.amalgamhomes.com/",
    telephone: "+302106742743",
    priceRange: "$$$",
    email: "info@amalgamhomes.com",
    address: [
      {
        "@type": "PostalAddress",
        addressRegion: "Paros",
        addressLocality: "Ampelas",
        addressCountry: "Greece",
        postalCode: "84400",
      },
      {
        "@type": "PostalAddress",
        addressRegion: "Mykonos",
        addressLocality: "Kalafatis",
        addressCountry: "Greece",
        postalCode: "84600",
      },
      {
        "@type": "PostalAddress",
        addressRegion: "Tinos",
        addressLocality: "Triantaros",
        addressCountry: "Greece",
        postalCode: "84700",
      },
      {
        "@type": "PostalAddress",
        addressRegion: "Naxos",
        addressLocality: "Kastraki",
        addressCountry: "Greece",
        postalCode: "84300",
      },
    ],
    geo: [
      {
        "@type": "GeoCoordinates",
        latitude: "37.09244023063562",
        longitude: "25.265257004009054",
      },
      {
        "@type": "GeoCoordinates",
        latitude: "37.440552836153735",
        longitude: "25.41149775929062",
      },
      {
        "@type": "GeoCoordinates",
        latitude: "37.55960990405813",
        longitude: "25.19320510532755",
      },
      {
        "@type": "GeoCoordinates",
        latitude: "37.001881",
        longitude: "25.387944",
      },
    ],
  }

  return (
    <>
      <title>{seo.title + " | Amalgam Homes Naxos Paros Mykonos Tinos"}</title>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={seo.title + " | Amalgam Homes Naxos Paros Mykonos Tinos"}
      />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:creator" content={seo.twitterUsername} />
      <meta name="og:url" content={seo.url} />
      <meta
        name="og:title"
        content={seo.title + " | Amalgam Homes Naxos Paros Mykonos Tinos"}
      />
      <meta name="og:description" content={seo.description} />
      <meta name="og:image" content={seo.image} />
      <meta name="og:type" content="website" />
      <meta name="og:site_name" content="Amalgam Homes" />
      <meta name="og:locale" content="en_US" />
      <meta
        name="google-site-verification"
        content="kSn_p2DN6RTNTWtQgy6t1Bl9VuwVEXofEoBCIeiLKqk"
      />
      <link rel="icon" type="image/x-icon" href="/static/favicon.ico" />
      {children}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgBrand)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgOrganization)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgWebSite)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgLocalBusiness)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgImageLogo)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgImagePrimary)}
      </script>
    </>
  )
}
